/* Accessible navigation; custom-element lifecycle also supports Shopify editor reloads. */
(() => {
  'use strict';
  if (customElements.get('kaya-navigation')) return;
  class KayaNavigation extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.items = [...this.querySelectorAll('[data-kt-disclosure]')];
      this.toggleButton = this.querySelector('[data-kt-toggle]');
      this.backdrop = this.querySelector('[data-kt-backdrop]');
      this.bar = this.querySelector('.kt-bar');
      this.mobileQuery = matchMedia('(max-width: 989px)');
      this.hoverQuery = matchMedia('(hover: hover) and (pointer: fine)');
      this.motionQuery = matchMedia('(prefers-reduced-motion: reduce)');
      this.active = null;
      this.mobileOpen = false;
      this.inertElements = new Map();
      this.timers = new Map();
      this.classList.add('is-ready');
      const on = (target, name, callback, options = {}) => target.addEventListener(name, callback, { ...options, signal: this.controller.signal });
      this.items.forEach(item => {
        const summary = item.querySelector('summary');
        item.open = false;
        item.querySelector('[data-kt-panel]').inert = true;
        summary.setAttribute('aria-expanded', 'false');
        on(summary, 'click', event => {
          event.preventDefault();
          this.clearTimer('hover');
          this.clearTimer('leave');
          if (this.active === item) this.closeActive();
          else this.openItem(item, this.mobileQuery.matches || item.hasAttribute('data-kt-search'));
        });
        on(summary, 'keydown', event => {
          if (event.key !== 'ArrowDown') return;
          event.preventDefault();
          this.openItem(item, true);
        });
        on(item, 'pointerenter', () => {
          this.clearTimer('leave');
          if (this.mobileQuery.matches || !this.hoverQuery.matches || this.dataset.openOnHover === 'false' || item.hasAttribute('data-kt-search')) return;
          this.delay('hover', () => this.openItem(item), 100);
        });
        on(item, 'pointerleave', () => this.clearTimer('hover'));
      });
      on(this.toggleButton, 'click', () => {
        if (this.mobileOpen || this.active) { this.closeAll(false, true); this.toggleButton.focus({ preventScroll: true }); return; }
        this.mobileOpen = true;
        this.syncState();
        this.querySelector('.kt-nav-trigger')?.focus({ preventScroll: true });
      });
      on(this.backdrop, 'click', () => this.closeAll(true));
      on(this.bar, 'pointerenter', () => this.clearTimer('leave'));
      on(this.bar, 'pointerleave', () => {
        this.clearTimer('hover');
        if (this.mobileQuery.matches || !this.hoverQuery.matches || !this.active || this.active.hasAttribute('data-kt-search')) return;
        if (this.active.querySelector('[data-kt-panel]').contains(document.activeElement)) return;
        this.delay('leave', () => this.closeActive(), 180);
      });
      this.querySelectorAll('.kt-nav-item>a').forEach(link => on(link, 'pointerenter', () => {
        if (!this.mobileQuery.matches && this.hoverQuery.matches && this.dataset.openOnHover !== 'false') this.closeActive();
      }));
      on(this, 'click', event => {
        if (event.target.closest('[data-kt-back]')) this.closeActive(true);
        else if (event.target.closest('a')) this.closeAll(false, true);
      });
      on(this, 'submit', () => this.closeAll(false, true));
      on(document, 'keydown', event => this.onKeydown(event));
      on(document, 'focusin', event => {
        if (!this.mobileQuery.matches && !this.contains(event.target)) this.closeActive();
      });
      on(document, 'pointerdown', event => {
        if ((this.active || this.mobileOpen) && !this.contains(event.target)) this.closeAll();
      });
      on(this.mobileQuery, 'change', () => {
        const hadFocus = this.contains(document.activeElement);
        this.closeAll(false, true);
        this.measure();
        if (hadFocus && this.mobileQuery.matches) this.toggleButton.focus({ preventScroll: true });
      });
      on(window, 'resize', () => this.queueMeasure(), { passive: true });
      on(window, 'scroll', () => this.queueMeasure(), { passive: true });
      on(window, 'pageshow', () => this.closeAll(false, true));
      if ('ResizeObserver' in window) {
        this.observer = new ResizeObserver(() => this.queueMeasure());
        this.observer.observe(this.bar);
        const announcement = this.querySelector('.kt-announcement');
        if (announcement) this.observer.observe(announcement);
        this.querySelectorAll('.kt-panel-content').forEach(content => this.observer.observe(content));
      }
      this.measure();
      this.syncState();
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.observer?.disconnect();
      this.timers?.forEach(timer => clearTimeout(timer));
      cancelAnimationFrame(this.measureFrame);
      cancelAnimationFrame(this.openFrame);
      this.measureFrame = null;
      this.unlockPage();
    }
    delay(name, callback, duration) {
      this.clearTimer(name);
      this.timers.set(name, setTimeout(() => { this.timers.delete(name); callback(); }, duration));
    }
    clearTimer(name) {
      clearTimeout(this.timers.get(name));
      this.timers.delete(name);
    }
    duration() { return this.motionQuery.matches || document.body.dataset.motion === 'false' ? 0 : 280; }
    finishClosing(item) {
      this.clearTimer(item);
      item.open = false;
      item.classList.remove('kt-closing');
      const panel = item.querySelector('[data-kt-panel]');
      panel.inert = true;
      panel.style.removeProperty('height');
      item.querySelector('summary').setAttribute('aria-expanded', 'false');
    }
    openItem(item, focus = false) {
      this.clearTimer('leave');
      this.clearTimer('hover');
      if (!item || this.active === item) { if (focus && item) this.focusPanel(item); return; }
      cancelAnimationFrame(this.openFrame);
      const oldHeight = this.active?.querySelector('[data-kt-panel]').getBoundingClientRect().height || 0;
      this.items.forEach(other => { if (other !== item) this.finishClosing(other); });
      this.clearTimer(item);
      item.classList.remove('kt-closing');
      this.active = item;
      if (item.hasAttribute('data-kt-search')) this.mobileOpen = false;
      const panel = item.querySelector('[data-kt-panel]');
      item.open = true;
      panel.inert = false;
      item.querySelector('summary').setAttribute('aria-expanded', 'true');
      this.updatePosition();
      if (!this.mobileQuery.matches) { panel.style.height = `${oldHeight}px`; panel.getBoundingClientRect(); }
      this.syncState();
      this.openFrame = requestAnimationFrame(() => {
        if (this.active !== item || !this.isConnected) return;
        this.fitPanel();
        if (focus) this.focusPanel(item);
      });
    }
    focusPanel(item) {
      const panel = item.querySelector('[data-kt-panel]');
      const first = panel.querySelector('[data-kt-search-input]') || this.focusable(panel)[0];
      first?.focus({ preventScroll: true });
    }
    closeActive(restoreFocus = false, immediate = false) {
      this.clearTimer('hover');
      this.clearTimer('leave');
      cancelAnimationFrame(this.openFrame);
      const item = this.active;
      if (!item) return;
      this.active = null;
      const summary = item.querySelector('summary');
      summary.inert = false;
      summary.setAttribute('aria-expanded', 'false');
      const panel = item.querySelector('[data-kt-panel]');
      if (restoreFocus) summary.focus({ preventScroll: true });
      panel.inert = true;
      if (immediate || !this.duration() || this.mobileQuery.matches) this.finishClosing(item);
      else {
        item.classList.add('kt-closing');
        panel.style.height = '0px';
        this.delay(item, () => this.finishClosing(item), this.duration());
      }
      this.syncState();
    }
    closeAll(restoreFocus = false, immediate = false) {
      const returnTo = this.mobileOpen ? this.toggleButton : this.active?.querySelector('summary');
      this.closeActive(false, immediate);
      if (immediate) this.items.forEach(item => this.finishClosing(item));
      this.mobileOpen = false;
      this.syncState();
      if (restoreFocus && returnTo) returnTo.focus({ preventScroll: true });
    }
    updatePosition() {
      const bottom = Math.max(0, Math.round(this.bar.getBoundingClientRect().bottom));
      this.style.setProperty('--kt-header-bottom', `${bottom}px`);
      this.headerBottom = bottom;
    }
    queueMeasure() {
      if (this.measureFrame) return;
      this.measureFrame = requestAnimationFrame(() => { this.measureFrame = null; this.measure(); });
    }
    measure() {
      if (!this.isConnected) return;
      this.updatePosition();
      const announcementHeight = this.querySelector('.kt-announcement')?.offsetHeight || 0;
      document.documentElement.style.setProperty('--rv-header-height', `${this.bar.offsetHeight + announcementHeight}px`);
      this.fitPanel();
    }
    fitPanel() {
      if (!this.active || this.mobileQuery.matches) return;
      const panel = this.active.querySelector('[data-kt-panel]');
      const height = Math.min(panel.querySelector('.kt-panel-content').scrollHeight, Math.max(0, innerHeight - this.headerBottom));
      if (panel.style.height !== `${height}px`) panel.style.height = `${height}px`;
    }
    syncState() {
      this.dataset.mobileOpen = String(this.mobileOpen);
      const mobileOverlay = this.mobileQuery.matches && (this.mobileOpen || !!this.active);
      this.toggleButton.setAttribute('aria-expanded', String(mobileOverlay));
      this.toggleButton.setAttribute('aria-label', mobileOverlay ? 'Menü schließen' : 'Menü öffnen');
      this.toggleButton.setAttribute('aria-controls', this.active?.hasAttribute('data-kt-search') ? this.active.querySelector('[data-kt-panel]').id : this.querySelector('[data-kt-navigation]').id);
      const submenuOpen = this.mobileQuery.matches && this.mobileOpen && !!this.active;
      this.querySelectorAll('.kt-nav-item').forEach(item => { item.inert = submenuOpen && !item.contains(this.active); });
      this.items.forEach(item => { item.querySelector('summary').inert = submenuOpen && item === this.active; });
      const search = this.querySelector('[data-kt-search]');
      search.querySelector('summary').setAttribute('aria-label', this.active === search ? 'Suche schließen' : 'Suche öffnen');
      const open = !!this.active || this.mobileOpen;
      this.clearTimer('backdrop');
      if (open) { this.backdrop.hidden = false; this.backdrop.classList.add('kt-visible'); }
      else {
        this.backdrop.classList.remove('kt-visible');
        this.delay('backdrop', () => { if (!this.active && !this.mobileOpen) this.backdrop.hidden = true; }, this.duration());
      }
      if (open && this.mobileQuery.matches) this.lockPage(); else this.unlockPage();
    }
    lockPage() {
      document.documentElement.classList.add('kt-nav-locked');
      document.querySelectorAll('#MainContent,.shopify-section-group-footer-group,[data-cart-toast]').forEach(element => {
        if (!this.inertElements.has(element)) this.inertElements.set(element, element.inert);
        element.inert = true;
      });
    }
    unlockPage() {
      document.documentElement.classList.remove('kt-nav-locked');
      this.inertElements?.forEach((original, element) => { element.inert = original; });
      this.inertElements?.clear();
    }
    focusable(root) {
      return [...root.querySelectorAll('a[href],button:not([disabled]):not([tabindex="-1"]),input:not([disabled]):not([type="hidden"]),summary,[tabindex="0"]')].filter(element => element.getClientRects().length && !element.closest('[inert]') && !element.closest('.kt-closing'));
    }
    onKeydown(event) {
      if (!this.active && !this.mobileOpen) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        if (this.active) this.closeActive(true); else this.closeAll(true);
      } else if (event.key === 'Tab' && this.mobileQuery.matches) {
        const elements = this.focusable(this);
        const first = elements[0], last = elements[elements.length - 1], current = document.activeElement;
        if (event.shiftKey && (current === first || !elements.includes(current))) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && (current === last || !elements.includes(current))) { event.preventDefault(); first?.focus(); }
      }
    }
  }
  customElements.define('kaya-navigation', KayaNavigation);
})();
